/**
 * Listing approval + next-action + provider presentation helpers.
 *
 * Pure and dependency-free (only a type-only import from `channels`). These map
 * canonical listing state into operator-facing labels and a single "what do I
 * do next" hint. They never recompute authoritative state and never invent a
 * workflow that the backend does not support.
 *
 * Readiness, approval, and publication are intentionally kept DISTINCT here —
 * a listing may legitimately be "Blocked" and "Approval required" and "Not
 * published" at the same time, and the UI must not collapse them into one fake
 * status.
 */

import type { ApprovalState } from "./channels";
import { shouldShowSyncChanges } from "./phase1-sync-contract";

// ---------------------------------------------------------------------------
// Approval presentation
// ---------------------------------------------------------------------------

export function approvalLabel(state: ApprovalState): string {
  switch (state) {
    case "approved": return "Approved";
    case "stale": return "Out of date";
    case "none": return "Approval required";
  }
}

export type ApprovalTone = "approved" | "warning" | "neutral";

export function approvalTone(state: ApprovalState): ApprovalTone {
  switch (state) {
    case "approved": return "approved";
    case "stale": return "warning";
    case "none": return "neutral";
  }
}

// ---------------------------------------------------------------------------
// Next action
// ---------------------------------------------------------------------------

export interface NextActionInput {
  /** Canonical readiness status (UPPERCASE, from listing_readiness_checks). */
  readiness: string | null | undefined;
  /** Version-bound approval state (from `approvalState`). */
  approval: ApprovalState;
  /** Listing lifecycle status (channel_listings.status). */
  status: string | null | undefined;
  /** Publication/sync status (channel_listings.publication_status). */
  publicationStatus: string | null | undefined;
  /** True when canonical data has drifted and needs a re-sync. */
  hasDirty: boolean;
  /** Optional domain list; when present, Sync changes is only for operator domains. */
  dirtyDomains?: string[] | null;
  /** True when the connector is authorized/connected. */
  connectionReady: boolean;
  /** Provider-confirmed remote listing (e.g. BigCommerce product id). */
  providerConfirmedListed?: boolean;
}

export interface NextAction {
  label: string;
  tone: "danger" | "warning" | "primary" | "neutral";
}

/**
 * Determine the single most useful operator next step, ordered by severity.
 * Returns `null` when no further action is determinable from the given state
 * (the listing is live and in sync, or is archived/ended).
 *
 * Ordering rationale: a blocked or failed listing is fixed before approval;
 * approval must happen before publication; publication is last.
 */
export function nextAction(input: NextActionInput): NextAction | null {
  const { readiness, approval, status, publicationStatus, hasDirty, dirtyDomains, connectionReady, providerConfirmedListed } = input;
  const operatorDirty = dirtyDomains ? shouldShowSyncChanges(dirtyDomains) : hasDirty;

  if (!connectionReady) {
    return { label: "Connect channel", tone: "warning" };
  }

  if (readiness === "BLOCKED") {
    return { label: "Fix listing", tone: "danger" };
  }

  if (status === "error" || publicationStatus === "sync_failed") {
    return { label: "Review issue", tone: "danger" };
  }

  if (approval === "none") {
    return { label: "Review & approve", tone: "warning" };
  }

  if (approval === "stale") {
    return { label: "Review changes", tone: "warning" };
  }

  const listed = publicationStatus === "published" || Boolean(providerConfirmedListed);
  const publishable = status === "draft" || status === "ready" || status === "paused";
  if (publishable && !listed) {
    return { label: "Publish", tone: "primary" };
  }

  if (listed && operatorDirty) {
    return { label: "Sync changes", tone: "warning" };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Provider display
// ---------------------------------------------------------------------------

/** Human-readable marketplace name for a connector provider id. */
export function providerLabel(provider: string | null | undefined): string {
  switch (provider) {
    case "ebay": return "eBay";
    case "bigcommerce": return "BigCommerce";
    case "amazon": return "Amazon";
    case "temu": return "Temu";
    case "shopify": return "Shopify";
    default: return provider ?? "";
  }
}